/**
 * List Drawer
 *
 * App-shell-level (not page-scoped) left-hand pull-out drawer for an
 * agent's Contact Lists (migration 071, per Todd 2026-10-30) - named
 * groups of contacts, addressable from Messaging's To:/Cc:/Bcc: fields
 * (see AgentStoryRecipient.listId). Replaces CartDrawer on the Contacts
 * page specifically (Cart stays available on Map/Messaging, where
 * property-collecting is still relevant; see main.ts's route-change
 * wiring) - Carts and Lists are peer concepts, not the same feature.
 *
 * Deliberately a SEPARATE component from CartDrawer, not a shared base
 * class, even though the two are meant to feel identical to the agent
 * (per Todd 2026-10-30: "the Agent learns one approach to management").
 * CartDrawer has real Cart-only complexity (polymorphic item_type with
 * per-item label resolution, drag-reorder/sort_order, an is_default/
 * Featured special-cased row) that Lists has no equivalent for - a
 * flat, unordered contact_id set. Retrofitting the already-shipped,
 * actively-used CartDrawer into a generic base purely to share with a
 * brand-new second consumer would risk regressing something that works
 * today, for an abstraction boundary that's genuinely unclear with
 * exactly two consumers. Instead, this file reuses CartDrawer's exact
 * CSS classes (via its exported injectCartDrawerStyles()) for every
 * piece of chrome that's visually identical - wrapper/handle/panel/
 * resize grip/list-header/"+ New" button/row+chevron/rename+delete
 * buttons/toast - so the two are pixel-identical and behaviorally
 * identical by construction, without coupling their logic. If a third
 * similar drawer shows up later, that's the right moment to extract a
 * real shared base.
 *
 * "Selected" (which list "Add to List" toggles into) and "expanded"
 * (which lists show their members inline) are independent state, same
 * split CartDrawer already has for selected/expanded carts.
 */
import type { AgentClient, AgentListSummary, AgentListDetail } from '@slowmaui/api-client';
import { injectCartDrawerStyles } from './CartDrawer';

export interface ListDrawerOptions {
  container: HTMLElement;
  client: AgentClient;
  onOpen?: () => void;
  onClose?: () => void;
  onResize?: (width: number) => void;
}

const DEFAULT_WIDTH = 340;
const MIN_WIDTH = 280;
const MAX_WIDTH_PERCENT = 0.9;
const WIDTH_STORAGE_KEY = 'listDrawerWidth';

export class ListDrawer {
  private container: HTMLElement;
  private client: AgentClient;
  private onOpen?: () => void;
  private onClose?: () => void;
  private onResize?: (width: number) => void;

  private wrapper!: HTMLElement;
  private handle!: HTMLElement;
  private panel!: HTMLElement;
  private body!: HTMLElement;
  private resizeEl!: HTMLElement;
  private opened = false;
  private available = false;
  private width: number;

  private lists: AgentListSummary[] = [];
  private listsLoaded = false;
  /** Which list "Add to List" adds to - distinct from which list(s) are
   *  expanded below. Unlike Cart, there's no guaranteed default list, so
   *  this can be null if the agent has never created one yet. */
  private selectedListId: string | null = null;
  private expandedListIds: Set<string> = new Set();
  private listDetails: Map<string, AgentListDetail> = new Map();
  private loadingListIds: Set<string> = new Set();
  /** Which list's name is currently showing as an inline <input> instead
   *  of static text - same mechanism/reasoning as CartDrawer's
   *  editingCartId (per Todd 2026-11-01). */
  private editingListId: string | null = null;

  constructor(options: ListDrawerOptions) {
    injectCartDrawerStyles();
    this.container = options.container;
    this.client = options.client;
    this.onOpen = options.onOpen;
    this.onClose = options.onClose;
    this.onResize = options.onResize;

    this.width = this.clampWidth(this.readStoredWidth() ?? DEFAULT_WIDTH);

    this.render();
    this.wireHandle();
    this.wireResize();
    this.renderBody();
  }

  // -------- Public API --------

  setAvailable(available: boolean): void {
    this.available = available;
    if (!available) this.close();
    this.wrapper.classList.toggle('cart-drawer-unavailable', !available);
  }

  open(): void {
    if (this.opened || !this.available) return;
    this.opened = true;
    this.wrapper.classList.add('open');
    this.panel.style.width = `${this.width}px`;
    this.handle.style.left = `${this.width}px`;
    this.handle.setAttribute('aria-expanded', 'true');
    this.onOpen?.();
  }

  close(): void {
    if (!this.opened) return;
    this.opened = false;
    this.wrapper.classList.remove('open');
    this.panel.style.width = '0';
    this.handle.style.left = '0';
    this.handle.setAttribute('aria-expanded', 'false');
    this.onClose?.();
  }

  toggle(): void {
    this.opened ? this.close() : this.open();
  }

  isOpen(): boolean {
    return this.opened;
  }

  getWidth(): number {
    return this.width;
  }

  /** Called by Contacts.ts's "Add to List" button/picker once an agent
   *  picks a contact to add. Adds to the given list if one's passed
   *  (the picker's multi-select "Done"), otherwise whichever list is
   *  currently selected in this drawer (the per-row icon's one-click
   *  path, skipping the picker entirely when a list is already
   *  selected - per Todd 2026-10-30). Unlike Cart there's no guaranteed
   *  default list, so with nothing selected and no listId given, this
   *  opens the drawer and just stops there rather than silently doing
   *  nothing. Always re-renders at the end (not just when the target
   *  list happens to be expanded) so the sidebar's member count/
   *  selected-tag never goes stale after an external add - this is
   *  also the fix for a real reported bug: Contacts.ts previously
   *  called the API directly, bypassing this drawer's cache entirely. */
  async addItem(contactId: string, label: string, listId?: string): Promise<void> {
    this.open();
    try {
      if (!this.listsLoaded) await this.loadLists();
      const targetId = listId ?? this.selectedListId;
      if (!targetId) {
        this.showToast('Create a list first');
        return;
      }
      await this.client.addListMember(targetId, { contactId });
      await this.refreshList(targetId);
      await this.loadLists();
      this.renderBody();
      this.showToast(`Added ${label}`);
    } catch (error) {
      console.error('[ListDrawer] Failed to add item:', error);
      this.showToast('Failed to add - try again');
    }
  }

  /** Whether a list is currently selected - Contacts.ts checks this to
   *  decide between a one-click add (skip the picker) and opening it. */
  getSelectedListId(): string | null {
    return this.selectedListId;
  }

  /** Public re-sync point for mutations made elsewhere that this drawer
   *  wouldn't otherwise know about (e.g. Contacts.ts's picker creating a
   *  brand-new list via the API directly, rather than this drawer's own
   *  "+ New" button). */
  async refreshLists(): Promise<void> {
    await this.loadLists();
    this.renderBody();
  }

  hasItem(contactId: string): boolean {
    const list = this.selectedListId ? this.listDetails.get(this.selectedListId) : null;
    return !!list?.members.some((m) => m.contact_id === contactId);
  }

  async toggleItem(contactId: string, label: string): Promise<void> {
    const list = this.selectedListId ? this.listDetails.get(this.selectedListId) : null;
    const existing = list?.members.find((m) => m.contact_id === contactId);
    if (existing && this.selectedListId) {
      await this.removeItem(this.selectedListId, contactId);
      this.showToast(`Removed ${label}`);
    } else {
      await this.addItem(contactId, label);
    }
  }

  showToast(message: string): void {
    const toast = document.createElement('div');
    toast.className = 'cart-drawer-toast';
    toast.textContent = message;
    document.body.appendChild(toast);
    requestAnimationFrame(() => toast.classList.add('show'));
    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => toast.remove(), 300);
    }, 2500);
  }

  destroy(): void {
    this.container.innerHTML = '';
  }

  // -------- Shell rendering --------

  private render(): void {
    this.container.innerHTML = `
      <div class="cart-drawer-wrapper cart-drawer-unavailable" id="list-drawer-wrapper">
        <div class="cart-drawer-handle" id="list-drawer-handle" title="Open / close your lists" aria-expanded="false">
          <span class="cart-drawer-handle-icon">📋</span>
          <svg class="cart-drawer-handle-arrow" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#000" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>
          <div class="cart-drawer-handle-label">Lists</div>
        </div>
        <div class="cart-drawer-panel" id="list-drawer-panel">
          <div class="cart-drawer-body" id="list-drawer-body"></div>
          <div class="cart-drawer-resize" id="list-drawer-resize" title="Drag to resize">
            <div class="cart-drawer-resize-grip"></div>
          </div>
        </div>
      </div>
    `;

    this.wrapper = this.container.querySelector('#list-drawer-wrapper') as HTMLElement;
    this.handle = this.container.querySelector('#list-drawer-handle') as HTMLElement;
    this.panel = this.container.querySelector('#list-drawer-panel') as HTMLElement;
    this.body = this.container.querySelector('#list-drawer-body') as HTMLElement;
    this.resizeEl = this.container.querySelector('#list-drawer-resize') as HTMLElement;
  }

  private wireHandle(): void {
    this.handle.addEventListener('click', () => this.toggle());
  }

  // Identical drag math to CartDrawer's own wireResize (same left-
  // anchored, grows-rightward panel shape) - see that file's comment.
  private wireResize(): void {
    let dragging = false;
    let startX = 0;
    let startWidth = 0;

    const onMouseDown = (e: MouseEvent) => {
      if (window.innerWidth < 768) return;
      dragging = true;
      startX = e.clientX;
      startWidth = this.width;
      this.resizeEl.classList.add('dragging');
      this.wrapper.classList.add('resizing');
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'ew-resize';
      e.preventDefault();
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!dragging) return;
      const delta = e.clientX - startX;
      this.setWidth(startWidth + delta);
    };

    const onMouseUp = () => {
      if (!dragging) return;
      dragging = false;
      this.resizeEl.classList.remove('dragging');
      this.wrapper.classList.remove('resizing');
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };

    this.resizeEl.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }

  private setWidth(width: number): void {
    this.width = this.clampWidth(width);
    if (this.opened) {
      this.panel.style.width = `${this.width}px`;
      this.handle.style.left = `${this.width}px`;
    }
    this.writeStoredWidth(this.width);
    this.onResize?.(this.width);
  }

  private clampWidth(w: number): number {
    const max = window.innerWidth * MAX_WIDTH_PERCENT;
    return Math.max(MIN_WIDTH, Math.min(max, w));
  }

  private readStoredWidth(): number | null {
    try {
      const raw = localStorage.getItem(WIDTH_STORAGE_KEY);
      if (!raw) return null;
      const n = parseInt(raw, 10);
      return Number.isFinite(n) ? n : null;
    } catch {
      return null;
    }
  }

  private writeStoredWidth(width: number): void {
    try {
      localStorage.setItem(WIDTH_STORAGE_KEY, String(Math.round(width)));
    } catch {
      // Ignore - resize still works for this session, just doesn't persist.
    }
  }

  // -------- Body content --------

  private renderBody(): void {
    if (!this.listsLoaded) {
      this.body.innerHTML = `<p class="cart-drawer-hint">Loading...</p>`;
      void this.loadLists().then(() => this.renderBody());
      return;
    }

    this.body.innerHTML = this.buildBodyContent();
    this.attachBodyListeners();
  }

  private buildBodyContent(): string {
    if (this.lists.length === 0) {
      return `
        <div class="cart-drawer-list-header">
          <span>Your Lists</span>
          <button type="button" class="cart-drawer-new-btn" title="Start a new list">+ New</button>
        </div>
        <p class="cart-drawer-hint">No lists yet - start one, then add contacts to it from the Contacts page.</p>
      `;
    }
    return `
      <div class="cart-drawer-list-header">
        <span>Your Lists</span>
        <button type="button" class="cart-drawer-new-btn" title="Start a new list">+ New</button>
      </div>
      <div class="cart-drawer-list">
        ${this.lists.map((l) => this.buildListRow(l)).join('')}
      </div>
    `;
  }

  private buildListRow(list: AgentListSummary): string {
    const isSelected = this.selectedListId === list.id;
    const isExpanded = this.expandedListIds.has(list.id);
    const isLoading = this.loadingListIds.has(list.id);
    const isEditing = this.editingListId === list.id;
    const detail = this.listDetails.get(list.id);

    const chevron = `<svg class="cart-drawer-chevron${isExpanded ? ' expanded' : ''}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>`;

    // Inline <input> instead of a prompt() popup - same mechanism as
    // CartDrawer's buildCartRow (per Todd 2026-11-01).
    const nameHtml = isEditing
      ? `<input type="text" class="cart-drawer-cart-name-input" value="${escapeAttr(list.name)}" maxlength="80">`
      : `<span class="cart-drawer-cart-name"><span class="cart-drawer-cart-name-text">${escapeHtml(list.name)}</span></span>`;

    return `
      <div class="cart-drawer-cart${isSelected ? ' selected' : ''}" data-list-id="${list.id}">
        <div class="cart-drawer-cart-row">
          <button type="button" class="cart-drawer-chevron-btn" title="${isExpanded ? 'Collapse' : 'Expand'}">${chevron}</button>
          ${nameHtml}
          <span class="cart-drawer-cart-count">${list.member_count}</span>
          ${!isEditing ? `
            <button type="button" class="cart-drawer-rename-btn" title="Rename this list">✏️</button>
            <button type="button" class="cart-drawer-delete-btn" title="Delete this list">🗑</button>
          ` : ''}
        </div>
        ${isSelected ? `<div class="cart-drawer-selected-tag">Add to List adds here</div>` : ''}
        ${isExpanded ? `<div class="cart-drawer-cart-items">${this.buildMembersHtml(detail, isLoading)}</div>` : ''}
      </div>
    `;
  }

  private buildMembersHtml(detail: AgentListDetail | undefined, isLoading: boolean): string {
    if (isLoading || !detail) return `<p class="cart-drawer-hint">Loading...</p>`;
    const members = detail.members;
    if (members.length === 0) {
      return `<p class="cart-drawer-hint">Nothing in this list yet - use "Add to List" from the Contacts page.</p>`;
    }

    return members.map((member) => `
      <div class="cart-drawer-item" data-list-id="${detail.id}" data-contact-id="${member.contact_id}">
        <div class="cart-drawer-item-row">
          <span class="cart-drawer-item-label">${escapeHtml(member.name || member.email)}</span>
        </div>
        <div class="cart-drawer-item-actions">
          <button type="button" class="cart-drawer-item-remove" title="Remove">Remove</button>
        </div>
      </div>
    `).join('');
  }

  private attachBodyListeners(): void {
    this.body.querySelector('.cart-drawer-new-btn')?.addEventListener('click', () => {
      void this.createNewList().then(() => this.renderBody());
    });

    this.body.querySelectorAll('.cart-drawer-cart').forEach((rowEl) => {
      const listId = (rowEl as HTMLElement).dataset.listId;
      if (!listId) return;

      rowEl.querySelector('.cart-drawer-cart-name')?.addEventListener('click', () => {
        void this.selectList(listId).then(() => this.renderBody());
      });
      rowEl.querySelector('.cart-drawer-cart-count')?.addEventListener('click', () => {
        void this.selectList(listId).then(() => this.renderBody());
      });

      rowEl.querySelector('.cart-drawer-chevron-btn')?.addEventListener('click', () => {
        void this.toggleExpanded(listId).then(() => this.renderBody());
      });

      rowEl.querySelector('.cart-drawer-rename-btn')?.addEventListener('click', () => {
        this.editingListId = listId;
        this.renderBody();
      });

      // Same inline-edit commit/cancel mechanism as CartDrawer's own
      // attachBodyListeners (per Todd 2026-11-01).
      const nameInput = rowEl.querySelector<HTMLInputElement>('.cart-drawer-cart-name-input');
      if (nameInput) {
        nameInput.focus();
        nameInput.select();
        let committed = false;
        const commit = () => {
          if (committed) return;
          committed = true;
          const list = this.lists.find((l) => l.id === listId);
          const name = nameInput.value.trim();
          this.editingListId = null;
          if (list && name && name !== list.name) {
            void this.renameList(listId, name).then(() => this.renderBody());
          } else {
            this.renderBody();
          }
        };
        nameInput.addEventListener('blur', commit);
        nameInput.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            nameInput.blur();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            committed = true;
            this.editingListId = null;
            this.renderBody();
          }
        });
      }

      rowEl.querySelector('.cart-drawer-delete-btn')?.addEventListener('click', () => {
        if (!confirm('Delete this list? This cannot be undone.')) return;
        void this.deleteList(listId).then(() => this.renderBody());
      });

      rowEl.querySelectorAll('.cart-drawer-item').forEach((itemRow) => {
        const contactId = (itemRow as HTMLElement).dataset.contactId;
        if (!contactId) return;

        itemRow.querySelector('.cart-drawer-item-remove')?.addEventListener('click', (e) => {
          e.stopPropagation();
          void this.removeItem(listId, contactId).then(() => this.renderBody());
        });
      });
    });
  }

  // -------- List data operations --------

  private async loadLists(): Promise<void> {
    try {
      this.lists = await this.client.getAgentLists();
      this.listsLoaded = true;
      if (!this.selectedListId || !this.lists.some((l) => l.id === this.selectedListId)) {
        const fallback = this.lists[0];
        if (fallback) await this.selectList(fallback.id, { expand: false });
        else this.selectedListId = null;
      }
    } catch (error) {
      console.error('[ListDrawer] Failed to load lists:', error);
      this.listsLoaded = true;
    }
  }

  private async createNewList(): Promise<void> {
    try {
      const { id } = await this.client.createAgentList();
      await this.loadLists();
      await this.selectList(id);
      // Starts straight in the inline-rename state (per Todd 2026-11-01) -
      // same reasoning as CartDrawer's createNewCart.
      this.editingListId = id;
    } catch (error) {
      console.error('[ListDrawer] Failed to create list:', error);
      this.showToast('Failed to create a new list');
    }
  }

  private async selectList(id: string, opts: { expand?: boolean } = {}): Promise<void> {
    this.selectedListId = id;
    if (opts.expand !== false && !this.expandedListIds.has(id)) {
      await this.expandList(id);
    } else if (!this.listDetails.has(id)) {
      await this.fetchListDetail(id);
    }
  }

  private async toggleExpanded(id: string): Promise<void> {
    if (this.expandedListIds.has(id)) {
      this.expandedListIds.delete(id);
    } else {
      await this.expandList(id);
    }
  }

  private async expandList(id: string): Promise<void> {
    this.expandedListIds.add(id);
    if (!this.listDetails.has(id)) {
      await this.fetchListDetail(id);
    }
  }

  private async fetchListDetail(id: string): Promise<void> {
    this.loadingListIds.add(id);
    try {
      const detail = await this.client.getAgentList(id);
      this.listDetails.set(id, detail);
    } catch (error) {
      console.error('[ListDrawer] Failed to load list:', error);
      this.listDetails.delete(id);
    } finally {
      this.loadingListIds.delete(id);
    }
  }

  private async renameList(id: string, name: string): Promise<void> {
    try {
      await this.client.updateAgentList(id, { name });
      await this.loadLists();
    } catch (error) {
      console.error('[ListDrawer] Failed to rename list:', error);
      this.showToast('Failed to rename list');
    }
  }

  private async deleteList(id: string): Promise<void> {
    try {
      await this.client.deleteAgentList(id);
      this.listDetails.delete(id);
      this.expandedListIds.delete(id);
      if (this.selectedListId === id) this.selectedListId = null;
      await this.loadLists();
    } catch (error) {
      console.error('[ListDrawer] Failed to delete list:', error);
      this.showToast('Failed to delete list');
    }
  }

  private async refreshList(id: string): Promise<void> {
    if (!this.expandedListIds.has(id)) {
      this.listDetails.delete(id);
      return;
    }
    await this.fetchListDetail(id);
    this.renderBody();
  }

  private async removeItem(listId: string, contactId: string): Promise<void> {
    try {
      await this.client.removeListMember(listId, contactId);
      await this.refreshList(listId);
      await this.loadLists();
    } catch (error) {
      console.error('[ListDrawer] Failed to remove member:', error);
      this.showToast('Failed to remove');
    }
  }
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

// Same gap/fix as CartDrawer's own escapeAttr - escapeHtml's roundtrip
// doesn't escape quotes, unsafe to reuse for an attribute value as-is.
function escapeAttr(text: string): string {
  return escapeHtml(text).replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
